import React, { useMemo } from 'react'
import { Button, Icon, Modal, Tag } from 'antd'
import { ImagePreview } from '../../../../../../components'
import { CREATIVE_GUIDELINE_SECTION_LABELS } from './constants'
import './FinalizedReportModal.scss'

const formatLabel = value =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase())

const getText = (...values) => {
  const value = values.find(item => typeof item === 'string' && item.trim())
  return value ? value.trim() : ''
}

const getAssetId = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '')

const getAssetTitle = asset =>
  formatLabel(
    getText(asset?.asset_type, asset?.assetType, asset?.parent_entity_type) ||
      'Visual'
  )

const buildAssetsById = assets => {
  const map = {}
  ;(assets || []).forEach(asset => {
    const id = getAssetId(asset.id, asset.asset_id, asset.assetId)
    if (id) map[String(id)] = asset
  })
  return map
}

const getAssetUrl = asset => asset?.image_url || asset?.imageUrl || ''

const getReportPayload = report => {
  if (!report || typeof report !== 'object') return {}

  const output =
    report.output && typeof report.output === 'object' ? report.output : null

  return (
    report.bible ||
    report.creative_bible ||
    report.creativeBible ||
    report.creative_guideline ||
    report.creativeGuideline ||
    report.guideline ||
    report.finalized_report ||
    report.finalizedReport ||
    report.report ||
    output?.bible ||
    output?.creative_bible ||
    output?.creativeBible ||
    output?.creativeGuideline ||
    output?.creative_guideline ||
    output?.final_bible ||
    output?.finalBible ||
    output?.guideline ||
    output?.report ||
    output ||
    report
  )
}

const AssetCard = ({ asset, title, subtitle }) => {
  if (!asset) return null

  return (
    <div className="finalized-report__asset-card">
      <div className="finalized-report__asset-image">
        {getAssetUrl(asset) ? (
          <ImagePreview
            src={getAssetUrl(asset)}
            alt={title || getAssetTitle(asset)}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Icon type="picture" />
        )}
      </div>
      <div>
        <strong>{title || getAssetTitle(asset)}</strong>
        {subtitle ? <span>{subtitle}</span> : null}
      </div>
    </div>
  )
}

const TextBlock = ({ title, children }) => {
  if (!children) return null

  return (
    <div className="finalized-report__text-block">
      <span>{title}</span>
      <p>{children}</p>
    </div>
  )
}

const Section = ({ eyebrow, title, children }) => (
  <section className="finalized-report__section">
    <div className="finalized-report__section-heading">
      {eyebrow ? <span>{eyebrow}</span> : null}
      <h3>{title}</h3>
    </div>
    {children}
  </section>
)

const EmptyState = ({ children }) => (
  <div className="finalized-report__empty">
    <Icon type="info-circle" />
    <span>{children}</span>
  </div>
)

const MoodArc = ({ items }) => {
  if (!items?.length) return null

  return (
    <div className="finalized-report__mood-arc">
      {items.map((item, index) => (
        <div
          className="finalized-report__mood-step"
          key={`${item.feeling}-${index}`}
        >
          <span>{Math.round(Number(item.position || 0) * 100)}%</span>
          <div>
            <strong>{item.feeling || `Beat ${index + 1}`}</strong>
            {item.note ? <p>{item.note}</p> : null}
          </div>
        </div>
      ))}
    </div>
  )
}

const CharacterCard = ({ character, assetsById }) => {
  const sheet = assetsById[String(character.character_sheet_asset_id)]
  const emotionSheet = assetsById[String(character.emotion_sheet_asset_id)]

  return (
    <article className="finalized-report__profile-card">
      <div className="finalized-report__profile-main">
        <div>
          <h4>{character.name || character.placeholder || 'Character'}</h4>
          <p>{character.role_in_story || 'No role summary provided.'}</p>
        </div>
        {character.user_verified ? <Tag color="green">Verified</Tag> : null}
      </div>
      <TextBlock title="Look">{character.physical_description}</TextBlock>
      <div className="finalized-report__asset-row">
        <AssetCard asset={sheet} title="Character Sheet" />
        <AssetCard asset={emotionSheet} title="Emotion Sheet" />
      </div>
    </article>
  )
}

const OutfitCard = ({ outfit, assetsById }) => {
  const sheet = assetsById[String(outfit.outfit_sheet_asset_id)]

  return (
    <article className="finalized-report__profile-card">
      <div className="finalized-report__profile-main">
        <div>
          <h4>{outfit.name || 'Outfit'}</h4>
          <p>{outfit.description || 'No outfit description provided.'}</p>
        </div>
        {outfit.user_verified ? <Tag color="green">Verified</Tag> : null}
      </div>
      <TextBlock title="Used In">{outfit.scene_context}</TextBlock>
      <div className="finalized-report__asset-row">
        <AssetCard asset={sheet} title="Outfit Sheet" />
      </div>
    </article>
  )
}

const LocationCard = ({ location, assetsById }) => {
  const composite = assetsById[String(location.location_composite_asset_id)]

  return (
    <article className="finalized-report__profile-card">
      <div className="finalized-report__profile-main">
        <div>
          <h4>{location.name || location.placeholder || 'Location'}</h4>
          <p>{location.description || 'No location description provided.'}</p>
        </div>
        {location.user_verified ? <Tag color="green">Verified</Tag> : null}
      </div>
      <div className="finalized-report__detail-grid">
        <TextBlock title="Atmosphere">{location.atmosphere}</TextBlock>
        <TextBlock title="Time">{location.time_of_day}</TextBlock>
        <TextBlock title="Weather">{location.weather}</TextBlock>
      </div>
      <div className="finalized-report__asset-row">
        <AssetCard asset={composite} title="Location Board" />
      </div>
    </article>
  )
}

const StoryboardShotList = ({ shots, panels }) => {
  const storyboardItems = shots?.length ? shots : panels || []

  if (!storyboardItems.length) {
    return <EmptyState>No storyboard plan was included in the report.</EmptyState>
  }

  return (
    <div className="finalized-report__shot-list">
      {storyboardItems.map((shot, index) => {
        const number = shot.panel_number || index + 1
        const title =
          shot.beat ||
          shot.scene_description ||
          shot.summary ||
          `Panel ${number}`
        const body = shot.summary || shot.action_description || ''
        const meta = [
          shot.shot_type,
          shot.camera_angle,
          shot.camera_movement,
          shot.mood
        ].filter(Boolean)

        return (
          <div className="finalized-report__shot" key={`${number}-${index}`}>
            <span>{number}</span>
            <div>
              <strong>{title}</strong>
              {body && body !== title ? <p>{body}</p> : null}
              {meta.length ? <small>{meta.join(' - ')}</small> : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const VisualAssetGrid = ({ assets }) => {
  if (!assets?.length) return null

  return (
    <div className="finalized-report__visual-grid">
      {assets.map(asset => (
        <div
          className="finalized-report__visual-card"
          key={asset.id || getAssetUrl(asset)}
        >
          <div className="finalized-report__visual-image">
            {getAssetUrl(asset) ? (
              <ImagePreview
                src={getAssetUrl(asset)}
                alt={getAssetTitle(asset)}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Icon type="picture" />
            )}
          </div>
          <div>
            <strong>{getAssetTitle(asset)}</strong>
            <span>
              {asset.parent_entity_id ||
                asset.parentEntityId ||
                asset.generation_status ||
                'Generated visual'}
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

const FinalizedReportModal = ({ visible, report, onClose }) => {
  const guidelineReport = useMemo(() => getReportPayload(report), [report])
  const assetsById = useMemo(
    () => buildAssetsById(guidelineReport.assets),
    [guidelineReport]
  )

  if (!report) return null

  const {
    story = {},
    vision = {},
    characters = [],
    outfits = [],
    locations = [],
    storyboard_plan: storyboardPlan = {},
    panels = [],
    assets = []
  } = guidelineReport

  const storyboardCompositeAssets = assets.filter(asset =>
    String(asset.asset_type || '').includes('storyboard')
  )

  return (
    <Modal
      className="finalized-report-modal"
      visible={visible}
      width={1120}
      title={null}
      footer={null}
      onCancel={onClose}
      destroyOnClose
    >
      <article className="finalized-report">
        <header className="finalized-report__hero">
          <div>
            <span>Finalized report</span>
            <h2>{CREATIVE_GUIDELINE_SECTION_LABELS.finalized}</h2>
            <p>
              Vision, characters, outfits, locations, storyboard, and visual
              references.
            </p>
          </div>
          <Button icon="close" onClick={onClose}>
            Close
          </Button>
        </header>

        <div className="finalized-report__summary">
          <div>
            <span>Format</span>
            <strong>{story.canvas_style || '16:9'}</strong>
          </div>
          <div>
            <span>Style</span>
            <strong>{story.visual_style || 'Visual style'}</strong>
          </div>
          <div>
            <span>{CREATIVE_GUIDELINE_SECTION_LABELS.characters}</span>
            <strong>{characters.length}</strong>
          </div>
          <div>
            <span>{CREATIVE_GUIDELINE_SECTION_LABELS.storyboard}</span>
            <strong>{storyboardPlan.proposed_count || panels.length || 0}</strong>
          </div>
        </div>

        <Section
          eyebrow="01"
          title={CREATIVE_GUIDELINE_SECTION_LABELS.vision}
        >
          <div className="finalized-report__statement">
            {vision.creative_statement ||
              'No creative statement was included in this report.'}
          </div>
          <div className="finalized-report__detail-grid">
            <TextBlock title="Emotional Core">{vision.emotional_core}</TextBlock>
            <TextBlock title="Audience Feeling">
              {vision.audience_feeling_goal}
            </TextBlock>
            <TextBlock title="Camera">{vision.camera_philosophy}</TextBlock>
            <TextBlock title="Color World">{vision.color_world}</TextBlock>
            <TextBlock title="Sound">{vision.sound_direction}</TextBlock>
            <TextBlock title="Pacing">{vision.pacing_rhythm}</TextBlock>
            <TextBlock title="Avoid">{vision.anti_vision}</TextBlock>
          </div>
          <MoodArc items={vision.mood_arc} />
        </Section>

        <Section
          eyebrow="02"
          title={CREATIVE_GUIDELINE_SECTION_LABELS.characters}
        >
          {characters.length ? (
            <div className="finalized-report__profile-grid">
              {characters.map(character => (
                <CharacterCard
                  character={character}
                  assetsById={assetsById}
                  key={character.id || character.character_key}
                />
              ))}
            </div>
          ) : (
            <EmptyState>No characters were included in the report.</EmptyState>
          )}
        </Section>

        <Section
          eyebrow="03"
          title={CREATIVE_GUIDELINE_SECTION_LABELS.outfits}
        >
          {outfits.length ? (
            <div className="finalized-report__profile-grid">
              {outfits.map(outfit => (
                <OutfitCard
                  outfit={outfit}
                  assetsById={assetsById}
                  key={outfit.id || outfit.outfit_key}
                />
              ))}
            </div>
          ) : (
            <EmptyState>
              No outfit guidance was included in the report.
            </EmptyState>
          )}
        </Section>

        <Section
          eyebrow="04"
          title={CREATIVE_GUIDELINE_SECTION_LABELS.locations}
        >
          {locations.length ? (
            <div className="finalized-report__profile-grid">
              {locations.map(location => (
                <LocationCard
                  location={location}
                  assetsById={assetsById}
                  key={location.id || location.location_key}
                />
              ))}
            </div>
          ) : (
            <EmptyState>No location guidance was included in the report.</EmptyState>
          )}
        </Section>

        <Section
          eyebrow="05"
          title={CREATIVE_GUIDELINE_SECTION_LABELS.storyboard}
        >
          <TextBlock title="Plan Rationale">{storyboardPlan.rationale}</TextBlock>
          <StoryboardShotList shots={storyboardPlan.shots} panels={panels} />
          {storyboardCompositeAssets.length ? (
            <>
              <div className="finalized-report__subheading">
                Storyboard Visuals
              </div>
              <VisualAssetGrid assets={storyboardCompositeAssets} />
            </>
          ) : null}
        </Section>

        <Section
          eyebrow="06"
          title={CREATIVE_GUIDELINE_SECTION_LABELS.finalized}
        >
          <div className="finalized-report__subheading">
            Generated Visual References
          </div>
          <VisualAssetGrid assets={assets} />
        </Section>
      </article>
    </Modal>
  )
}

export default FinalizedReportModal
